import {
  ensure,
  wrapErrors,
  asAString,
  asAInteger,
  anyOf,
  beBetween,
  ensureProperties,
  itsProperty,
  validateNotOverlapingOf,
  invalid,
} from "../Common/composition";
import SelectInput from "common/SelectInput";
import AddFileSignaturesModal from "./AddFileSignaturesModal";
import AddPredefinedSignaturesModal from "./AddPredefinedSignaturesModal";

const validatePriority = wrapErrors(
  (error) => `Priority ${error}`,
  ensure(asAInteger(beBetween(-9999, 9999)))
);

const signatureTypes = [
  { value: "HTTPS-SNI", label: "HTTPS-SNI", cli: "https sni", pattern: true },
  { value: "HTTP-host", label: "HTTP-host", cli: "http host", pattern: true },
  { value: "QUIC-SNI", label: "QUIC-SNI", cli: "quic sni", pattern: true },
  {
    value: "P2P-FILESHARING",
    label: "P2P-FILESHARING",
    cli: "p2p filesharing",
  },
  {
    value: "SPEEDTEST-OOKLA",
    label: "SPEEDTEST-OOKLA",
    cli: "speedtest ookla",
  },
];

const findType = (type) =>
  signatureTypes.find(({ value }) => value === type) ||
  invalid(`unknown signature type ${type}`);

const signatureTypeNames = signatureTypes.map(({ value }) => value);

const validateDPIType = wrapErrors(
  (error) => `Type ${error}`,
  ensure(asAString(anyOf(...signatureTypeNames)))
);

const asteriskMeansNone = (pattern) => (pattern === "*" ? "" : pattern);

const cantContainChars = (forbidden) => (pattern) =>
  Array.from(pattern).find((char) => forbidden.includes(char)) === undefined
    ? pattern
    : invalid("contains invalid characters");

const validateDPIPattern = wrapErrors(
  (error) => `Pattern ${error}`,
  ensure(asteriskMeansNone, cantContainChars(" ?<>!$%&()='\\/[]"))
);

const patternEmptyForCertainTypes = ({ type, pattern, ...signature }) =>
  findType(type).pattern === true || pattern.length === 0
    ? { type, pattern, ...signature }
    : invalid(`Pattern must be empty for type "${type}"`);

const patternNotEmptyForCertainTypes = ({ type, pattern, ...signature }) =>
  findType(type).pattern === true && pattern.length === 0
    ? invalid(`Pattern must not be empty for type "${type}"`)
    : { type, pattern, ...signature };

const describe = ({ pattern, type }) =>
  pattern.length > 0 ? `${type} on "${pattern}"` : `${type}`;

const compare = (one, another) =>
  one.pattern === another.pattern && one.type === another.type;

const verifySignature = wrapErrors(
  (error) => `Signature ${error}`,
  ensure(
    ensureProperties(
      itsProperty("type", validateDPIType),
      itsProperty("pattern", validateDPIPattern)
    ),
    patternEmptyForCertainTypes,
    patternNotEmptyForCertainTypes,
    validateNotOverlapingOf(compare, describe)
  )
);

const parseTypeAndPattern = (line) => {
  const [first, second] = line.trim().split(/\s+/);
  const [type, pattern = ""] = second === undefined
    ? [first, second]
    : [second, first];
  return { pattern, type };
};

const signatureActions = [
  {
    label: "Add Signatures File",
    signal: "add-signatures-file",
    modal: AddFileSignaturesModal,
  },
  {
    label: "Add Predefined Signatures",
    signal: "add-predefined-signatures",
    modal: AddPredefinedSignaturesModal,
  },
];

const assertTypeAndPattern = ({ type, pattern }) =>
  (({ cli, pattern: expectsPattern }) =>
    expectsPattern === true ? [`${cli} '${pattern}'`] : [cli])(findType(type));

const assertSignatures = ({ added = [], keeped = [], updated = [] }) => [
  ...[...added, ...keeped, ...updated].map(assertTypeAndPattern),
];

const dpiComposition = {
  cliType: "dpi",
  typeLabel: "DPI",
  viewURL: "viewDPIProfileConfig",
  redeclareToUpdate: true,
  fields: [
    { name: "name", label: "Profile name", type: "text", canUpdate: false },
    {
      name: "signatures",
      cliName: "signature",
      label: "signatures",
      labelSingle: "Signature",
      addActionLabel: "Custom Signature",
      type: "many",
      validate: verifySignature,
      customActions: signatureActions,
      importLineParse: parseTypeAndPattern,
      canBeEmpty: false,
      cliExpressAs: assertSignatures /* profile is cleared before */,
      fields: [
        {
          name: "pattern",
          label: "pattern",
          labelSingle: "DPI Pattern",
          colHeadClass: "text-left",
          type: "text",
          icon: "cloud_queue",
        },
        {
          name: "type",
          label: "Type",
          colHeadClass: "text-left",
          type: "select",
          icon: "style",
          defaultValue: "HTTPS-SNI",
          InputComponent: SelectInput,
          options: signatureTypes,
          validate: validateDPIType,
        },
      ],
    },
    {
      name: "priority",
      label: "Priority",
      type: "number",
      defaultValue: "9999",
      idx: 3, // Fixes after "many" grouping issue
      icon: "label_outline",
      validate: validatePriority,
      min: -9999,
      max: 9999,
    },
  ],
};

export default dpiComposition;
